import { FC, useEffect, useState } from 'react';
import { Box, Modal, Svg } from '@hopskipdrive/react-components';
import { analyticsClickAddRiderLocation } from '@rideiq/api/services/Analytics';
import { Button } from '@rideiq/shared/components/Button';

import { RiderLocation } from '../Location/Location.types';
import { RiderLocationForm } from '../RiderLocationForm';

import { CloseButton } from './AddLocation.styles';

const riderLocationDefaults = {
  primary: false,
  mustBeMet: false,
  mustBeMetProtocol: {
    locationProtocol: '',
    authorizedContactGroups: [],
  },
  locationId: undefined,
  locationProtocol: '',
  replaceLocation: undefined,
};

interface AddLocationProps {
  addLocation: (data, locationType) => Promise<boolean>;
  associatedLocations: RiderLocation[];
  isRiderActive: boolean;
  riderSlug: string;
}

export const AddLocation: FC<AddLocationProps> = ({ addLocation, associatedLocations, isRiderActive, riderSlug }) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleAddLocation = async (data, locationType) => {
    setIsLoading(true);
    const response = await addLocation(data, locationType);

    response && setIsModalOpen(!response);
    setIsLoading(false);
  };

  useEffect(() => {
    isModalOpen && analyticsClickAddRiderLocation();
  }, [isModalOpen]);

  return (
    <>
      <Presentation
        isOpen={isModalOpen}
        associatedLocations={associatedLocations}
        isLoading={isLoading}
        onCancel={() => setIsModalOpen(false)}
        onSubmit={handleAddLocation}
        riderSlug={riderSlug}
      />
      {isRiderActive ? (
        <Button
          color="secondary"
          onClick={() => setIsModalOpen(true)}
          data-testid="add-location"
          size="small"
          style={{ minWidth: '68px', height: '25px' }}
          aria-label="Add location for rider"
        >
          Add
        </Button>
      ) : null}
    </>
  );
};

const Presentation = ({ isOpen, associatedLocations, isLoading, onCancel, onSubmit, riderSlug }) => {
  const disabledIds = associatedLocations?.map((location) => location.id.toString());

  return (
    <Modal open={isOpen} data-testid="rider-location-add-modal" maxWidth={'xl'}>
      <Modal.Title>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          Add Location to Rider
          <CloseButton onClick={onCancel} data-testid="closeButton" aria-label="Close rider add location modal">
            <Svg height="20px" icon="close-outlined" />
          </CloseButton>
        </Box>
      </Modal.Title>
      <Modal.Content>
        <RiderLocationForm
          initialValues={riderLocationDefaults}
          onSubmit={onSubmit}
          disabledLocationIds={disabledIds}
          associatedLocations={associatedLocations}
          riderSlug={riderSlug}
          onCancel={onCancel}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button color="tertiary" onClick={onCancel} loading={isLoading}>
          Cancel
        </Button>
        <Button color="primary" type="submit" form={'rider-associated-location'} loading={isLoading}>
          {isLoading ? 'Adding...' : 'Add'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
